import { Children, useState } from "react";
import { createPortal } from "react-dom";

import checkMark from "../../assets/images/svgs/check-mark-1.svg";
import infoCircle from "../../assets/images/svgs/info_circle.svg";
import { DBT_NAME } from "../../constant";

const EverythingPlus = ({ prevPlanName }) => (
  <div className="bg-blue-500 bg-opacity-10 rounded-[26.67px] border-r-2 px-3">
    <div className="text-blue-500 text-[20px] font-medium font-['Lexend'] leading-10">
      Everything in {prevPlanName} +
    </div>
  </div>
);

const PlanBullets = ({ children }) => (
  <div className="w-full h-full justify-start items-center space-x-2 inline-flex flex-col">
    {Children.map(children, (child, index) => (
      <div className="w-full h-full justify-start items-start space-x-2 inline-flex">
        {index === 0 && (
          <div className="mt-[2px]">
            <img src={checkMark} alt="check-mark" />
          </div>
        )}
        <div className="w-72 opacity-80 text-[#082247] text-lg font-light leading-6 break-words">
          {child}
        </div>
      </div>
    ))}
  </div>
);

const CustomTooltip = ({ children, text }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = (e) => {
    const rect = e.target.getBoundingClientRect();
    setPosition({
      x: rect.left + rect.width / 2,
      y: rect.top - 10
    });
    setIsVisible(true);
  };

  const handleMouseLeave = () => {
    setIsVisible(false);
  };

  return (
    <>
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        style={{ display: 'inline-block' }}
      >
        {children}
      </span>
      {isVisible && createPortal(
        <div
          style={{
            position: 'fixed',
            left: position.x,
            top: position.y,
            transform: 'translate(-50%, -100%)',
            backgroundColor: 'white',
            color: 'black',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
            zIndex: 99999,
            padding: '12px',
            fontSize: '14px',
            lineHeight: '1.4',
            maxWidth: '300px',
            fontFamily: 'Lexend',
            wordWrap: 'break-word',
            whiteSpace: 'normal'
          }}
        >
          {text}
        </div>,
        document.body
      )}
    </>
  );
};

export const Plans = [
  {
    title: {
      text: "Community",
      pos: "first",
      btn: {
        text: "Sign Up for Free",
        link: "https://app.myaltimate.com/register",
      },
    },
    price: "$0/month",
    executions: [
      <PlanBullets>
        <>
          Starts with <b>200</b> credits grant
        </>
      </PlanBullets>,
      <PlanBullets>
        <>
          After first month, limits at <b>100</b> credits per month
        </>
      </PlanBullets>,
    ],
    functionality: [
      <PlanBullets>Datamates</PlanBullets>,
      <PlanBullets>Knowledge Hub (public links only)</PlanBullets>,
      <PlanBullets>Memory Hub</PlanBullets>,
      <PlanBullets>dbt models, docs, tests generation</PlanBullets>,
      <PlanBullets>Query translation, explanation, preview</PlanBullets>,
      <PlanBullets>Real-time column lineage</PlanBullets>,
      <PlanBullets>Defer to prod (local)</PlanBullets>,
      <PlanBullets>Project governance (local)</PlanBullets>,
      <PlanBullets>Smart-complete and quick navigation</PlanBullets>,
      <PlanBullets>
        <div className="flex flex-row gap-2 items-center text-[#082247] opacity-80">
          {DBT_NAME} Cloud integration
          <CustomTooltip text="dbt™ Cloud integration is treated in a same way as dbt™ Core integration. There are no credits counted just for dbt™ Cloud integration.">
            <img
              src={infoCircle}
              className="cursor-pointer"
              alt="info-circle"
            />
          </CustomTooltip>
        </div>
      </PlanBullets>,
      <PlanBullets>Access to preview features</PlanBullets>,
    ],
    aiAgentInterfaces: [
      <PlanBullets>VSCode</PlanBullets>,
      <PlanBullets>Python package</PlanBullets>,
      <PlanBullets>Cursor</PlanBullets>,
      <PlanBullets>Claude Code</PlanBullets>,
      <PlanBullets>Cline</PlanBullets>,
      <PlanBullets>Windsurf</PlanBullets>,
    ],
    support: [<PlanBullets>Community slack Channel</PlanBullets>],
  },
  {
    title: {
      text: "Pro",
      pos: "",
      btn: {
        text: "Sign Up & Get Subscription",
        link: "https://app.myaltimate.com/register",
      },
    },
    price: "Starts at $29/month",
    executions: [
      <PlanBullets>
        <>
          Starts at <b>200</b> credits per month
        </>
      </PlanBullets>,
      <PlanBullets>
        <>
          Limits at <b>500</b> credits per month
        </>
      </PlanBullets>,
    ],
    functionality: [<EverythingPlus prevPlanName="Community" />],
    aiAgentInterfaces: [
      <PlanBullets>VSCode</PlanBullets>,
      <PlanBullets>Python package</PlanBullets>,
      <PlanBullets>Cursor</PlanBullets>,
      <PlanBullets>Claude Code</PlanBullets>,
      <PlanBullets>Cline</PlanBullets>,
      <PlanBullets>Windsurf</PlanBullets>,
    ],
    support: [<PlanBullets>Community slack channel</PlanBullets>],
  },
  {
    title: {
      text: "Enterprise",
      pos: "last",
      btn: {
        text: "Contact Us",
        link: "https://calendly.com/d/crs2-7g8-xgm/enterprise-plan-pricing",
      },
    },
    price: "Custom",
    executions: [
      <PlanBullets>Custom no. of credits</PlanBullets>,
      <PlanBullets>Annual credits limit</PlanBullets>,
    ],
    functionality: [
      <EverythingPlus prevPlanName="Pro" />,
      <PlanBullets>Custom Datamates - Python based</PlanBullets>,
      <PlanBullets>Knowledge Hub (Private Link)</PlanBullets>,
      <PlanBullets>Custom Datamates - OpenAPI based</PlanBullets>,
      <PlanBullets>Custom Datamates - BYO MCP</PlanBullets>,
      <PlanBullets>Defer to prod (SaaS)</PlanBullets>,
      <PlanBullets>Project governance (SaaS)</PlanBullets>,
      <PlanBullets>Additional AI agents</PlanBullets>,
      <PlanBullets>Workload Migration Agents</PlanBullets>,
      <PlanBullets>Tableau cost savings module</PlanBullets>,
      <PlanBullets>Warehouse cost savings module</PlanBullets>,
      <PlanBullets>
        Enterprise security (RBAC, Configurable security, SSO)
      </PlanBullets>,
    ],
    aiAgentInterfaces: [
      <PlanBullets>VSCode</PlanBullets>,
      <PlanBullets>Python package</PlanBullets>,
      <PlanBullets>Cursor</PlanBullets>,
      <PlanBullets>Claude Code</PlanBullets>,
      <PlanBullets>Cline</PlanBullets>,
      <PlanBullets>Windsurf</PlanBullets>,
      <PlanBullets>SaaS UI</PlanBullets>,
      <PlanBullets>Slack</PlanBullets>,
      <PlanBullets>API support</PlanBullets>,
    ],
    support: [
      <PlanBullets>Dedicated Slack Channel</PlanBullets>,
      <PlanBullets>Support over Chatbot</PlanBullets>,
      <PlanBullets>Support over Video</PlanBullets>,
      <PlanBullets>Priority Support with SLAs</PlanBullets>,
      <PlanBullets>Custom Contracts (MSA, DPA)</PlanBullets>,
      <PlanBullets>Security review</PlanBullets>,
      <PlanBullets>Training Services</PlanBullets>,
    ],
  },
];
